// src/components/sidebar/SidebarLocality.jsx
// Context-specific sidebar for Locality context.
// Desktop front page (>=1200px) no longer renders this — its content now lives in the
// Directory Mode Column (see frontpage/columns/DirectoryColumn.jsx). Still used by the
// mobile sidebar panel (isMobilePanel) and any non-front-page desktop locality view — a live,
// reachable surface with the same three Directory sections as the desktop column.
// Reworked June 2026: weather → town image → directory nav → official contacts → utilities.
// Phase 2 (June 2026): directory fetch lifted into useDirectoryListings, shared with the
// desktop Directory column.
//
// Directory column shell brief, Scope 3 (16 Aug): the three sections below are now an exclusive
// concertina, same as the desktop column — Directory open on load, exactly one open at a time,
// the open one switches away rather than closes. Section state is lifted to this component's own
// top level (own useExpandInPlace instance, own handleToggleSection), matching DirectoryColumn's
// pattern for that level. Deliberately NOT lifted here: the deeper category/listing state inside
// each section, which stays local to DirectoryNav/ContactList/UtilityCategoryList exactly as it
// already was — this component never shared that state across sections the way DirectoryColumn
// does, and Scope 3 doesn't change that. One consequence, disclosed rather than fixed here: a
// category left open inside a section can still show open when the user switches back to that
// section, since nothing here clears it on section-switch (DirectoryColumn's handleToggleSection
// clears it because it owns that state; this component doesn't).

import React, { useState, useCallback } from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import SidebarContextWidget from '../core/SidebarContextWidget';
import TownImage from '../frontpage/TownImage';
import ContactList from '../frontpage/ContactList';
import DirectoryNav from '../frontpage/DirectoryNav';
import UtilityCategoryList from '../frontpage/UtilityCategoryList';
import SuggestListingModal from '../frontpage/SuggestListingModal';
import SuggestionReviewQueue from '../frontpage/SuggestionReviewQueue';
import { useDirectoryListings } from '../../hooks/useDirectoryListings';
import { useExpandInPlace } from '../../hooks/useExpandInPlace';

const SidebarLocality = ({
  currentMode,
  onModeSwitch,
  articles = [],
  chats = [],
  events = [],
  userHomeLocationName,
  userHomeLocationCoords,
  hasFooterOverlay = false,
  selectedArea = null,
  currentUser = null,
  guestFallbackBoundaryId = null,
  visitBoundaryId = null   // active temporary visit (Change 3) — wins over selectedArea/home
}) => {
  const { sidebarWidth, isSidebarCompact } = useMediaQuery();
  const { t } = useTranslation('frontpage');
  const theme = useTheme();

  // Shared directory data (listings, hero image, steward/pending) — same fetch as the
  // desktop Directory column.
  const {
    boundaryId,
    contacts,
    utilities,
    directoryListings,
    heroImageUrl,
    heroImageCredit,
    canReviewSuggestions,
    pendingSuggestions,
    setPendingSuggestions,
    refreshPending
  } = useDirectoryListings({ selectedArea, currentUser, guestFallbackBoundaryId, visitBoundaryId });

  // Modal UI state (component-owned)
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestModalListing, setSuggestModalListing] = useState(null);
  const [showReviewQueue, setShowReviewQueue] = useState(false);

  // Gate the form-open boundary behind auth, as DirectoryColumn does for the same CTA and
  // correction link: guests see the CTA and get the login hint on click, rather than an open
  // form they can't submit. (Previously the CTA was hidden on `currentUser &&`, which is truthy
  // for the fresh-load guest object, and the correction path was ungated.)
  const { requireAuth } = useAuth();

  const handleSuggestNew = useCallback(() => {
    requireAuth('gate_body_suggest_listing', () => {
      setSuggestModalListing(null);
      setShowSuggestModal(true);
    });
  }, [requireAuth]);

  const handleSuggestCorrection = useCallback((listing) => {
    requireAuth('gate_body_suggest_listing', () => {
      setSuggestModalListing(listing);
      setShowSuggestModal(true);
    });
  }, [requireAuth]);

  // Section-level concertina state (Scope 3) — Directory open on load; the open section cannot
  // be closed, only switched away from (handleToggleSection no-ops on the already-open id, same
  // guard as DirectoryColumn's own).
  const sectionGroup = useExpandInPlace({ defaultOpenId: 'directory' });
  const openSection = sectionGroup.openId;
  const handleToggleSection = useCallback((id) => {
    if (id === sectionGroup.openId) return;
    sectionGroup.toggle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionGroup.openId, sectionGroup.toggle]);

  const paddingNum = isSidebarCompact ? 12 : 16;
  const padding = `${paddingNum}px`;

  return (
    <div style={{
      width: `${sidebarWidth}px`,
      minWidth: `${sidebarWidth}px`,
      maxWidth: `${sidebarWidth}px`,
      background: '#EDF2F8',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      transition: 'width 0.3s ease'
    }}>
      {/* TOP SECTION - Context Widget (Weather) — unchanged */}
      <div style={{
        height: 'auto',
        paddingLeft: padding,
        paddingRight: padding,
        paddingBottom: padding,
        paddingTop: '0px',
        flexShrink: 0,
        transition: 'height 0.3s ease, padding 0.3s ease'
      }}>
        <SidebarContextWidget
          currentContext="locality"
          userHomeLocationName={userHomeLocationName}
          userHomeLocationCoords={userHomeLocationCoords}
          compact={true}
        />
      </div>

      {/* BOTTOM SECTION - Directory & contacts stack */}
      <div style={{
        flex: 1,
        minHeight: 0,
        paddingLeft: padding,
        paddingRight: padding,
        paddingBottom: hasFooterOverlay ? '120px' : padding,
        paddingTop: '0px',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Town photo — from hero_image_url or placeholder */}
        <TownImage
          src={heroImageUrl}
          credit={heroImageCredit}
          compact={true}
        />

        {/* 8-category directory navigation — one of three exclusive concertina sections
            (Scope 3); its own SectionTitle is now the section's click target. */}
        <DirectoryNav
          compact={true}
          listings={directoryListings}
          onSuggestCorrection={handleSuggestCorrection}
          isOpen={openSection === 'directory'}
          onToggleSection={() => handleToggleSection('directory')}
        />

        {/* Official contacts — merged list: the locality's own plus every ancestor boundary's
            (município-tier ruling), pre-sorted by directory_authority_categories rank then
            alphabetically (directoryService.js). No separate heading per tier — position and
            the entity names themselves carry the authority distinction. */}
        <ContactList
          title={t('official_contacts')}
          contacts={contacts}
          compact={true}
          isOpen={openSection === 'official_contact'}
          onToggleSection={() => handleToggleSection('official_contact')}
        />

        {/* Utility contacts — category-based with provider pop-outs */}
        <UtilityCategoryList
          categories={utilities}
          compact={true}
          isOpen={openSection === 'utility'}
          onToggleSection={() => handleToggleSection('utility')}
        />

        {/* Suggest a listing CTA */}
        {boundaryId && (
          <button
            onClick={handleSuggestNew}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              width: '100%',
              padding: '8px 12px',
              marginTop: '12px',
              background: 'white',
              border: '1px dashed #a0c4e8',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '12px',
              color: theme.primary,
              fontWeight: '600',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f7ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
          >
            + {t('suggest_listing')}
          </button>
        )}

        {/* Steward / admin pending suggestions badge — clickable to open review queue */}
        {canReviewSuggestions && pendingSuggestions > 0 && (
          <button
            onClick={() => setShowReviewQueue(true)}
            style={{
              display: 'block',
              width: '100%',
              marginTop: '8px',
              padding: '6px 10px',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '11px',
              color: '#856404',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fff0b3'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff3cd'; }}
          >
            {pendingSuggestions} {t('pending_suggestions')}
          </button>
        )}
      </div>

      {/* Suggest listing modal */}
      {showSuggestModal && (
        <SuggestListingModal
          boundaryId={boundaryId}
          listing={suggestModalListing}
          canReviewSuggestions={canReviewSuggestions}
          onClose={() => setShowSuggestModal(false)}
          onSubmitted={() => {
            setShowSuggestModal(false);
            // Refresh pending count for Stewards/admins
            refreshPending();
          }}
        />
      )}

      {/* Suggestion review queue modal (Stewards + admins) */}
      {showReviewQueue && (
        <SuggestionReviewQueue
          onClose={() => setShowReviewQueue(false)}
          onCountChange={(delta) => {
            setPendingSuggestions(prev => Math.max(0, prev + delta));
          }}
        />
      )}
    </div>
  );
};

export default SidebarLocality;
